/**
 * API client for the NCCN medical-necessity guideline evaluation endpoints.
 *
 * Wraps the five ai-service routes under `POST /medical/guidelines/*`:
 * fetch (by order / by codes), submit review, recompute, and get latest.
 * Every call attaches the in-memory bearer token for authorization.
 * @module nccnEvaluation
 */
import { getBearerToken } from "@risalabs_frontend_org/auth-wrapper";
import {
  GetNCCNEvaluationRequest,
  GetNCCNEvaluationResponse,
  NCCNEvaluationPayload,
  RecomputeNCCNEvaluationRequest,
  SubmitNCCNEvaluationRequest,
  SubmitNCCNEvaluationResponse,
} from "../../data-model/nccnEvaluation";
import {
  FetchNCCNGuidelinesDirectRequest,
  FetchNCCNGuidelinesRequest,
  FetchNCCNGuidelinesResponse,
} from "../../data-model/nccnGuidelines";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";

/**
 * POSTs a JSON payload to an ai-service endpoint with a bearer token.
 *
 * @param url - Fully-qualified endpoint URL
 * @param payload - Request body serialized to JSON
 * @param context - Label used when logging failures
 * @returns The parsed JSON response typed as `TResponse`
 * @throws {Error} When the token is missing or the response is non-OK
 */
const postWithAuth = async <TResponse>(
  url: string,
  payload: unknown,
  context: string,
): Promise<TResponse> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return (await response.json()) as TResponse;
  } catch (error) {
    logError(error as Error, context);
    throw error;
  }
};

/**
 * Fetches NCCN guidelines for an order, resolving codes from the order document.
 *
 * @param payload - Order ID plus optional decompose/evaluate/force-fresh flags
 * @returns The NCCN guideline records and (optional) evaluation
 * @throws {Error} When the request fails or returns a non-OK status
 */
export const fetchNccnGuidelinesForOrder = (
  payload: FetchNCCNGuidelinesRequest,
): Promise<FetchNCCNGuidelinesResponse> =>
  postWithAuth<FetchNCCNGuidelinesResponse>(
    API_ENDPOINTS.FETCH_NCCN_GUIDELINES_FOR_ORDER,
    payload,
    "Error fetching NCCN guidelines for order",
  );

/**
 * Fetches NCCN guidelines directly from J-codes, ICD code, and optional regimen.
 *
 * @param payload - J-codes, ICD code, and optional regimen/flags
 * @returns The NCCN guideline records and (optional) evaluation
 * @throws {Error} When the request fails or returns a non-OK status
 */
export const fetchNccnGuidelinesDirect = (
  payload: FetchNCCNGuidelinesDirectRequest,
): Promise<FetchNCCNGuidelinesResponse> =>
  postWithAuth<FetchNCCNGuidelinesResponse>(
    API_ENDPOINTS.FETCH_NCCN_GUIDELINES_DIRECT,
    payload,
    "Error fetching NCCN guidelines by codes",
  );

/**
 * Submits a human-reviewed NCCN guideline evaluation.
 *
 * @param payload - Order/patient/user IDs and the annotated evaluation payload
 * @returns Success confirmation with the order ID and status
 * @throws {Error} When the request fails or returns a non-OK status
 */
export const submitNccnEvaluation = (
  payload: SubmitNCCNEvaluationRequest,
): Promise<SubmitNCCNEvaluationResponse> =>
  postWithAuth<SubmitNCCNEvaluationResponse>(
    API_ENDPOINTS.SUBMIT_NCCN_EVALUATION,
    payload,
    "Error submitting NCCN evaluation",
  );

/**
 * Recomputes evaluation results bottom-up after a reviewer edits item verdicts.
 *
 * No LLM calls — re-derives each guideline's `final_result` and the order-level
 * `order_evaluation_result` from the (possibly edited) item verdicts.
 *
 * @param payload - Evaluation payload with user-modified item verdicts
 * @returns The recomputed evaluation payload
 * @throws {Error} When the request fails or returns a non-OK status
 */
export const recomputeNccnEvaluation = (
  payload: RecomputeNCCNEvaluationRequest,
): Promise<NCCNEvaluationPayload> =>
  postWithAuth<NCCNEvaluationPayload>(
    API_ENDPOINTS.RECOMPUTE_NCCN_EVALUATION,
    payload,
    "Error recomputing NCCN evaluation",
  );

/**
 * Fetches the latest stored NCCN guideline evaluation for an order.
 *
 * @param payload - Order ID and optional org ID for RLS-scoped reads
 * @returns The latest evaluation or a not-found indicator (`found: false`)
 * @throws {Error} When the request fails or returns a non-OK status
 */
export const getNccnEvaluation = (
  payload: GetNCCNEvaluationRequest,
): Promise<GetNCCNEvaluationResponse> =>
  postWithAuth<GetNCCNEvaluationResponse>(
    API_ENDPOINTS.GET_NCCN_EVALUATION,
    payload,
    "Error fetching NCCN evaluation",
  );
